import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, FileText, Loader2, X } from "lucide-react";

import { getWorkerDetail } from "@/features/ops/api/opsApi";
import { useOpsStore } from "@/features/ops/model/opsStore";
import type { WorkerStatus } from "@/features/ops/types";
import {
  DocumentPreviewModal,
  type DocPreviewState,
} from "@/features/ops/ui/components/DocumentPreviewModal";
import { certTone } from "@/features/ops/ui/components/workerReview";
import {
  PermitExpiryBadge,
  SinBadge,
} from "@/features/ops/ui/components/workerReviewBits";
import { Badge, type BadgeProps } from "@/shared/ui";
import {
  EXPERIENCE_LEVEL_LABELS,
  LEGAL_STATUS_LABELS,
} from "@/features/workers/types";

const STATUS_VARIANT: Record<WorkerStatus, BadgeProps["variant"]> = {
  PENDING: "warning",
  APPROVED: "success",
  SUSPENDED: "warning",
};

const BG_CHECK_LABELS: Record<string, string> = {
  REQUESTED: "Requested",
  IN_PROGRESS: "In progress",
  CLEAR: "Clear",
  FLAGGED: "Flagged",
  ERROR: "Error",
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Read-only full profile for any worker, opened from the ops "All workers"
 * list. Same review payload as the pending queue — lets ops re-check an
 * approved worker's info, documents, and certs at any time.
 */
export function WorkerDetailModal({
  workerId,
  onClose,
}: {
  workerId: string;
  onClose: () => void;
}) {
  const token = useOpsStore((s) => s.token)!;
  const [docPreview, setDocPreview] = useState<DocPreviewState | null>(null);

  const { data: w, isLoading, error } = useQuery({
    queryKey: ["ops", "worker-detail", workerId],
    queryFn: () => getWorkerDetail(token, workerId),
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Let the nested document preview consume Escape first.
      if (e.key === "Escape" && !docPreview) onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, docPreview]);

  const displayName = w
    ? `${w.first_name} ${w.last_name}`.trim() || w.name || w.email
    : "";

  return (
    <div
      className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-overlay/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl animate-scale-in flex-col overflow-hidden rounded-xl border border-border bg-surface-raised shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <p className="truncate text-sm font-semibold text-copy">
            Worker profile{displayName ? ` — ${displayName}` : ""}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-surface hover:bg-surface-sunken"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="flex items-center gap-2 p-6 text-sm text-copy-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading profile…
            </div>
          )}
          {error != null && (
            <div className="p-6 text-sm text-destructive">Could not load this worker.</div>
          )}

          {w && (
            <div className="flex items-start gap-4">
              <div className="h-20 w-20 shrink-0 overflow-hidden rounded-full border border-border bg-surface-sunken">
                {w.photo_url ? (
                  <img src={w.photo_url} alt={w.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-copy-subtle">
                    No photo
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-base font-semibold text-copy">{displayName}</p>
                  <Badge variant={STATUS_VARIANT[w.status]} dot>
                    {w.status}
                  </Badge>
                </div>

                <div className="flex flex-wrap gap-2">
                  {w.legal_status ? (
                    <span className="inline-flex items-center rounded-full border border-primary bg-primary-light px-2 py-0.5 text-xs font-medium text-primary">
                      {LEGAL_STATUS_LABELS[w.legal_status]}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full border border-warning bg-warning-light px-2 py-0.5 text-xs font-medium text-warning-foreground">
                      Legal status missing
                    </span>
                  )}
                  {w.experience_level && (
                    <span className="inline-flex items-center rounded-full border border-border bg-surface-sunken px-2 py-0.5 text-xs font-medium text-copy">
                      {EXPERIENCE_LEVEL_LABELS[w.experience_level]}
                    </span>
                  )}
                  <PermitExpiryBadge expiresOn={w.work_permit_expires_on} />
                  <SinBadge provided={w.sin_provided} />
                  {w.latest_background_check && (
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                        w.latest_background_check.state === "CLEAR"
                          ? "border-success bg-success-light text-success-foreground"
                          : w.latest_background_check.state === "FLAGGED" ||
                              w.latest_background_check.state === "ERROR"
                            ? "border-destructive bg-destructive-light text-destructive-foreground"
                            : "border-border bg-surface-sunken text-copy-muted"
                      }`}
                      title={w.latest_background_check.result_summary ?? undefined}
                    >
                      Check: {BG_CHECK_LABELS[w.latest_background_check.state]}
                    </span>
                  )}
                </div>

                <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
                  <div className="flex gap-2">
                    <dt className="text-copy-muted">Email</dt>
                    <dd className="break-all text-copy">{w.email}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-copy-muted">Phone</dt>
                    <dd className="text-copy">{w.phone || "Not provided"}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-copy-muted">City</dt>
                    <dd className="text-copy">{w.city || "Not set"}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-copy-muted">Min hourly rate</dt>
                    <dd className="text-copy">
                      {w.min_hourly_rate ? `$${w.min_hourly_rate}/h` : "No floor set"}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-copy-muted">Shifts worked</dt>
                    <dd className="text-copy">
                      {w.shift_count}
                      {w.rating_avg != null && (
                        <span className="ml-1 text-copy-muted">· ★ {w.rating_avg.toFixed(1)}</span>
                      )}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-copy-muted">Joined</dt>
                    <dd className="text-copy">{fmtDate(w.created_at)}</dd>
                  </div>
                  {w.approved_at && (
                    <div className="flex gap-2">
                      <dt className="text-copy-muted">Approved</dt>
                      <dd className="text-copy">{fmtDate(w.approved_at)}</dd>
                    </div>
                  )}
                </dl>

                {w.bio && (
                  <p className="rounded-md border border-border bg-surface-sunken px-3 py-2 text-xs text-copy">
                    {w.bio}
                  </p>
                )}

                <div>
                  <p className="mb-1 text-xs font-medium text-copy-muted">ID document</p>
                  {w.eligibility_document_url ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setDocPreview({
                            url: w.eligibility_document_url!,
                            workerName: displayName,
                          })
                        }
                        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1 text-xs text-primary hover:bg-surface-sunken"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        View uploaded proof
                      </button>
                      <a
                        href={w.eligibility_document_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs text-copy-muted hover:bg-surface-sunken"
                      >
                        <ExternalLink className="h-3 w-3" /> Open in new tab
                      </a>
                      {w.eligibility_document_uploaded_at && (
                        <span className="text-xs text-copy-subtle">
                          Uploaded {fmtDate(w.eligibility_document_uploaded_at)}
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-copy-subtle">Not uploaded</p>
                  )}
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium text-copy-muted">Certifications</p>
                  {w.certifications.length === 0 ? (
                    <p className="text-xs text-copy-subtle">None uploaded</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {w.certifications.map((cert) => {
                        const tone = certTone(cert);
                        return (
                          <a
                            key={cert.id}
                            href={cert.url}
                            target="_blank"
                            rel="noreferrer"
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs hover:underline ${tone.classes}`}
                          >
                            <span className="font-medium">{cert.category ?? cert.name}</span>
                            <span className="opacity-80">· {tone.label}</span>
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {docPreview && (
        // Clicks inside the nested preview must not bubble to this modal's
        // own overlay — that would close both layers at once.
        <div onClick={(e) => e.stopPropagation()}>
          <DocumentPreviewModal state={docPreview} onClose={() => setDocPreview(null)} />
        </div>
      )}
    </div>
  );
}
